import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api-client';
import type { HomePageId } from '@/pages/dashboard/home/home-registry';

export interface HomePreference {
  /** A home-page id from the registry, or null for "let the app decide". */
  defaultHomePage: HomePageId | null;
}

export const HOME_PREFERENCE_KEY = ['me', 'preferences'] as const;

/** The signed-in user's saved default home page (`GET /me/preferences`). */
export function useHomePreference() {
  return useQuery({
    queryKey: HOME_PREFERENCE_KEY,
    queryFn: () => api.get<{ data: HomePreference }>('/me/preferences'),
    staleTime: 5 * 60_000,
  });
}

/**
 * Saves the default home page. The cache is updated optimistically so the
 * home switcher flips at once; a rejected save rolls it back.
 */
export function useSetHomePreference() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (defaultHomePage: HomePageId | null) =>
      api.put<{ data: HomePreference }>('/me/preferences', { defaultHomePage }),
    onMutate: async (defaultHomePage) => {
      await qc.cancelQueries({ queryKey: HOME_PREFERENCE_KEY });
      const previous = qc.getQueryData<{ data: HomePreference }>(HOME_PREFERENCE_KEY);
      qc.setQueryData(HOME_PREFERENCE_KEY, { data: { defaultHomePage } });
      return { previous };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.previous) qc.setQueryData(HOME_PREFERENCE_KEY, ctx.previous);
    },
    onSuccess: (res) => {
      qc.setQueryData(HOME_PREFERENCE_KEY, res);
    },
  });
}
